import { Description, Image, MusicNote, SvgIconComponent, Videocam } from '@mui/icons-material';

import { DATE_FORMATS } from '@dw/constants';
import { Dayjs, dayjs } from '@dw/dayjs';
import { CLASS_FILE_LIMITS, CLASS_FILE_MEDIA_KIND, IDanceClassFile } from '@dw/models/danceClassFile';
import { formatFileSize as formatBytes, plurify } from '@dw/utils';

/**
 * The visual identity of one kind of file.
 *
 * Hexes rather than palette names because these are the FILL of a tile, not a theme colour slot —
 * they go on via `sx`, and they are the SAME values the mobile app uses, so a file that is purple
 * for a dancer is purple for the studio that posted it.
 */
export interface IClassFileAccent {
  Icon: SvgIconComponent;
  /** Tile background. */
  tint: string;
  /** Glyph and label colour, contrast-checked against `tint`. */
  ink: string;
  label: string;
}

const ACCENTS: Record<CLASS_FILE_MEDIA_KIND, IClassFileAccent> = {
  [CLASS_FILE_MEDIA_KIND.VIDEO]: { Icon: Videocam, tint: '#E7E0F4', ink: '#4C1D95', label: 'Video' },
  [CLASS_FILE_MEDIA_KIND.IMAGE]: { Icon: Image, tint: '#DDEFE4', ink: '#0F5132', label: 'Photo' },
  [CLASS_FILE_MEDIA_KIND.AUDIO]: { Icon: MusicNote, tint: '#FBEFC9', ink: '#7A4B00', label: 'Audio' },
  [CLASS_FILE_MEDIA_KIND.DOCUMENT]: { Icon: Description, tint: '#DCE9F5', ink: '#0B3A5D', label: 'Document' }
};

/**
 * Never throws and never returns undefined: a newer API can send a kind this build has never heard
 * of, and a file with no tile at all is worse than a generic one.
 */
export const getClassFileAccent = (mediaKind?: CLASS_FILE_MEDIA_KIND): IClassFileAccent =>
  (mediaKind && ACCENTS[mediaKind]) || ACCENTS[CLASS_FILE_MEDIA_KIND.DOCUMENT];

/**
 * The same derivation the API applies, for files the portal knows about before the API has seen
 * them — a picked file being sized and previewed on the upload modal.
 */
export const resolveMediaKind = (contentType?: string): CLASS_FILE_MEDIA_KIND => {
  const mediaType = (contentType ?? '').split(';')[0].trim().toLowerCase();

  if (mediaType.startsWith('video/')) return CLASS_FILE_MEDIA_KIND.VIDEO;
  if (mediaType.startsWith('image/')) return CLASS_FILE_MEDIA_KIND.IMAGE;
  if (mediaType.startsWith('audio/')) return CLASS_FILE_MEDIA_KIND.AUDIO;

  return CLASS_FILE_MEDIA_KIND.DOCUMENT;
};

/**
 * A file's size, said the way the rest of the portal says it.
 *
 * Deliberately the shared `formatFileSize` rather than a second implementation: an attachment on a
 * notice and a video on a class are the same fact to a studio, and two formatters would eventually
 * disagree about what 1.5MB is called. The guard is the only thing added — a size the API has not
 * sent yet renders as nothing rather than as "0kb".
 */
export const formatFileSize = (sizeInBytes?: number): string =>
  !sizeInBytes || sizeInBytes < 0 ? '' : formatBytes(sizeInBytes);

export const formatDuration = (durationSeconds?: number): string | undefined => {
  if (!durationSeconds || durationSeconds <= 0) {
    return undefined;
  }

  const total = Math.round(durationSeconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const padded = (value: number) => `${value}`.padStart(2, '0');

  return hours > 0
    ? `${hours}:${padded(minutes)}:${padded(seconds)}`
    : `${minutes}:${padded(seconds)}`;
};

/**
 * Everything a studio realistically shares, in the form an `<input accept>` takes: a MIME type, a
 * `/*` prefix, or a file extension. `video/*` leads because that is the common case.
 */
export const ACCEPTED_CLASS_FILE_TYPES = 'video/*,image/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt';

/**
 * Whether a file is a kind a studio may share.
 *
 * Enforced in code rather than left to the picker, because `accept` is only a HINT: every file
 * dialog offers an "All Files" escape, and a file DROPPED on the zone never passes through the
 * dialog at all. Without this the two ways of choosing a file would disagree, and a studio could
 * share a .psd that no dancer can open.
 */
export const isAcceptedClassFileType = (file: File): boolean => {
  const mediaType = (file.type ?? '').split(';')[0].trim().toLowerCase();
  const fileName = file.name.toLowerCase();

  return ACCEPTED_CLASS_FILE_TYPES.split(',').some((entry) => {
    const accepted = entry.trim().toLowerCase();

    if (accepted.startsWith('.')) {
      return fileName.endsWith(accepted);
    }

    if (accepted.endsWith('/*')) {
      return mediaType.startsWith(accepted.slice(0, -1));
    }

    return mediaType === accepted;
  });
};

/**
 * Checked BEFORE a transfer starts. The API enforces the same caps from S3 metadata, but only
 * after the bytes have arrived — refusing here is what stops a 600MB video crossing the wire only
 * to be rejected at the far end.
 */
export const exceedsSizeLimit = (sizeInBytes: number, mediaKind: CLASS_FILE_MEDIA_KIND): boolean =>
  sizeInBytes > (mediaKind === CLASS_FILE_MEDIA_KIND.VIDEO
    ? CLASS_FILE_LIMITS.MaxVideoSizeBytes
    : CLASS_FILE_LIMITS.MaxOtherFileSizeBytes);

/**
 * What a file is CALLED when it is being counted — "3 photos", "1 track".
 *
 * Separate from `IClassFileAccent.label`, which names one file ("Photo", "Audio") on a tile. A
 * counted noun has to pluralise sensibly, and "2 audios" does not; a studio counts tracks.
 */
const COUNT_NOUNS: Record<CLASS_FILE_MEDIA_KIND, string> = {
  [CLASS_FILE_MEDIA_KIND.IMAGE]: 'photo',
  [CLASS_FILE_MEDIA_KIND.VIDEO]: 'video',
  [CLASS_FILE_MEDIA_KIND.AUDIO]: 'track',
  [CLASS_FILE_MEDIA_KIND.DOCUMENT]: 'document'
};

/** Photos first, because a studio posts more of them than anything else. */
const COUNT_ORDER = [
  CLASS_FILE_MEDIA_KIND.IMAGE,
  CLASS_FILE_MEDIA_KIND.VIDEO,
  CLASS_FILE_MEDIA_KIND.AUDIO,
  CLASS_FILE_MEDIA_KIND.DOCUMENT
];

/**
 * What is inside one upload, in words — "4 photos · 1 video".
 *
 * The tiles show the files; this NAMES them, which is what a card needs when its description is
 * missing and what a reader needs when six tiles are too small to tell a document from a track.
 */
export const summariseClassFiles = (files: IDanceClassFile[]): string => {
  const counts = new Map<CLASS_FILE_MEDIA_KIND, number>();

  for (const file of files) {
    const kind = file.mediaKind && COUNT_NOUNS[file.mediaKind]
      ? file.mediaKind
      : CLASS_FILE_MEDIA_KIND.DOCUMENT;

    counts.set(kind, (counts.get(kind) ?? 0) + 1);
  }

  return COUNT_ORDER
    .filter(kind => counts.has(kind))
    .map(kind => {
      const count = counts.get(kind)!;
      return `${count} ${plurify(COUNT_NOUNS[kind], count)}`;
    })
    .join(' · ');
};

/**
 * The file's type, in the four or so characters people recognise it by.
 *
 * A document tile shows this rather than a glyph alone, because "PDF" and "DOCX" answer the
 * question the glyph raises — what is going to open when I click it.
 */
export const getFileExtension = (fileName?: string): string => {
  const extension = (fileName ?? '').split('.').pop() ?? '';

  return extension && extension.length <= 4 && !extension.includes(' ')
    ? extension.toUpperCase()
    : '';
};

/**
 * When a post went up, said the way a person would.
 *
 * The clock time is always there, whatever the day: "we shared this" is a different fact before and
 * after tonight's class, and "Today" on its own cannot tell a teacher which.
 *
 * Written against `isSame(…, 'day')` rather than dayjs's `isToday`/`isYesterday` plugins, which the
 * portal does not load — the plugin would be a global side effect for two comparisons.
 */
export const formatClassFileTimestamp = (timestamp?: Dayjs): string => {
  if (!timestamp) {
    return '';
  }

  const now = dayjs();
  const time = timestamp.format(DATE_FORMATS.HourMinute);

  if (timestamp.isSame(now, 'day')) return `Today, ${time}`;
  if (timestamp.isSame(now.subtract(1, 'day'), 'day')) return `Yesterday, ${time}`;
  if (timestamp.year() !== now.year()) return timestamp.format(DATE_FORMATS.FullDateTime);

  return timestamp.format(DATE_FORMATS.DayMonthHour);
};

/**
 * How many bars an audio tile draws.
 *
 * Nine, and the number is measured rather than chosen: a small tile is about a hundred pixels
 * across and the wave sits in two thirds of that, so fourteen bars and their gaps left each bar a
 * hairline wide. Nine keeps them solid enough to read as sound at arm's length.
 */
const WAVEFORM_BAR_COUNT = 9;

/** Nothing collapses to a hairline — a bar at zero reads as a rendering fault, not as quiet. */
const WAVEFORM_MIN_HEIGHT = 0.3;

/**
 * A stable set of bar heights for an audio tile, derived from the file's own id.
 *
 * This is an IDENTITY, not a signal. The portal never decodes the audio, so these peaks are not
 * loudness and are never labelled as such — what they do is make a track look like a track at a
 * glance, and make two tracks look like two different things rather than one repeated icon.
 *
 * Derived rather than random for a reason that shows immediately: the list refetches, and a random
 * waveform would reshuffle on every repaint, so a settled screen would visibly twitch.
 */
export const waveformBars = (seed: string, count: number = WAVEFORM_BAR_COUNT): number[] => {
  // FNV-1a over the seed, then xorshift per bar. Cheap, no dependency, and stable across platforms.
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    hash = Math.imul(hash ^ seed.charCodeAt(i), 16777619);
  }

  return Array.from({ length: count }, () => {
    hash = Math.imul(hash ^ (hash >>> 15), 2246822507);
    hash = Math.imul(hash ^ (hash >>> 13), 3266489909);

    const unit = ((hash >>> 0) % 1000) / 1000;

    return WAVEFORM_MIN_HEIGHT + unit * (1 - WAVEFORM_MIN_HEIGHT);
  });
};

/**
 * How large a file tile is drawn.
 *
 * An enum rather than a `'small' | 'large'` union, per the portal's fixed-set rule — and it lives
 * here rather than beside the component so the upload modal can name a tile size before an
 * `IDanceClassFile` exists at all.
 */
export enum CLASS_FILE_TILE_SIZE {
  SMALL = 'small',
  LARGE = 'large'
}

/** One upload, as the list renders it. */
export interface IClassFileGroup {
  uploadBatchId: string;
  description?: string;
  uploadedByName?: string;
  createdTimestamp?: Dayjs;
  files: IDanceClassFile[];
}

/**
 * Files folded into the uploads they arrived in, newest first.
 *
 * A file with NO batch id gets a group of its own keyed on its file id rather than joining a shared
 * "no batch" bucket — merging unrelated files would put one file's description on another's.
 *
 * Nothing here marks a group as unread. The dancer surfaces read against a "last seen" cursor, but a
 * studio does not have unread files of its own — a New chip on every card it posted itself says
 * nothing — so the portal deliberately has no such concept to compute.
 */
export const groupClassFiles = (files: IDanceClassFile[] | undefined): IClassFileGroup[] => {
  const grouped = new Map<string, IClassFileGroup>();

  for (const file of files ?? []) {
    const key = file.uploadBatchId ?? `file:${file.fileId}`;

    const existing = grouped.get(key);
    if (existing) {
      existing.files.push(file);
      continue;
    }

    grouped.set(key, {
      uploadBatchId: key,
      description: file.description,
      uploadedByName: file.uploadedByName,
      createdTimestamp: file.createdTimestamp,
      files: [file]
    });
  }

  return Array.from(grouped.values()).sort((a, b) => {
    if (!a.createdTimestamp || !b.createdTimestamp) {
      return 0;
    }
    return b.createdTimestamp.valueOf() - a.createdTimestamp.valueOf();
  });
};
